import { useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { nip19 } from 'nostr-tools';
import { useSeoMeta } from '@unhead/react';
import type { NostrEvent } from '@nostrify/nostrify';
import { BookOpen, Calendar, ExternalLink, Link2, PenSquare, Tag, User, WifiOff } from 'lucide-react';
import { Layout } from '@/components/Layout';
import { ContentBody } from '@/components/ContentBody';
import { ContentFormatSelect } from '@/components/ContentFormatSelect';
import { StarButton } from '@/components/StarButton';
import { ZapButton } from '@/components/ZapButton';
import { ShareDialog } from '@/components/ShareDialog';
import { CommentsDialog } from '@/components/CommentsDialog';
import { HighlightToggle } from '@/components/HighlightToggle';
import { HighlightLayer } from '@/components/HighlightLayer';
import { ReaderModeToggle } from '@/components/ReaderModeToggle';
import { ReaderModeView } from '@/components/ReaderModeView';
import { ExportMenu } from '@/components/ExportMenu';
import { PublicationExportMenu } from '@/components/PublicationExportMenu';
import { EventDetailsDialog } from '@/components/EventDetailsDialog';
import { OfflinePublicationSaveButton } from '@/components/OfflinePublicationSaveButton';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Card, CardContent } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { useAuthor } from '@/hooks/useAuthor';
import { useContentFormat, type ViewFormat } from '@/hooks/useContentFormat';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { usePublicationIndex, usePublicationSections } from '@/hooks/usePublications';
import { useOfflinePublication } from '@/hooks/useOfflinePublications';
import { sanitizeUrl } from '@/lib/sanitizeUrl';
import { getTagValue } from '@/lib/nip54';
import {
  PUBLICATION_CONTENT_KIND,
  PUBLICATION_INDEX_KIND,
  getDerivativeWorkInfo,
  getPublicationAuthors,
  getPublicationIdentifiers,
  getPublicationTopics,
  identifierUrl,
  publicationImage,
  publicationPublishedBy,
  publicationPublishedOn,
  publicationSource,
  publicationSummary,
  publicationTitle,
  publicationType,
  publicationVersion,
  sectionTitle,
} from '@/lib/nkbip01';
import NotFound from '@/pages/NotFound';

export function PublicationPage() {
  const { d: rawD, npub, sectionD } = useParams<{ d: string; npub: string; sectionD?: string }>();
  const { user } = useCurrentUser();

  let pubkey: string | undefined;
  try {
    const decoded = npub ? nip19.decode(npub) : undefined;
    if (decoded?.type === 'npub') pubkey = decoded.data;
  } catch {
    pubkey = undefined;
  }

  const d = rawD ? decodeURIComponent(rawD) : undefined;
  const { data: fetchedIndex, isLoading: indexLoading } = usePublicationIndex(pubkey, d);
  const { data: fetchedSections, isLoading: sectionsFetching } = usePublicationSections(fetchedIndex);

  // Fall back to the locally-saved copy when the relays don't have (or
  // can't be reached for) this publication — the point of saving it.
  // The fetched copy still wins when available, so a saved copy never
  // masks a newer published edit.
  const offline = useOfflinePublication(pubkey, d);
  const index = fetchedIndex ?? offline?.index;
  const isOfflineCopy = !fetchedIndex && Boolean(offline);

  // Sections follow whichever index is being displayed: mixing the
  // saved index with freshly-fetched sections (or vice versa) could
  // show a table of contents that doesn't match its content.
  const sections = isOfflineCopy ? offline?.sections : fetchedSections;
  const sectionsLoading = isOfflineCopy ? false : sectionsFetching;

  const author = useAuthor(index?.pubkey);
  const displayName = author.data?.metadata?.name ?? (index ? `${index.pubkey.slice(0, 8)}…` : '');

  const activeSection = sectionD
    ? sections?.find((s) => s.tags.find(([n]) => n === 'd')?.[1] === decodeURIComponent(sectionD))
    : undefined;
  const displayedSection = activeSection ?? sections?.[0];

  useSeoMeta({
    title: index ? `${publicationTitle(index)} — Wikistar` : 'Wikistar',
    description: index ? publicationSummary(index) : undefined,
  });

  if (!d || !npub || !pubkey) return <NotFound />;

  // Never show a loading skeleton over content we already hold locally.
  const isLoading = !offline && (indexLoading || (Boolean(index) && sectionsLoading));
  const isOwn = Boolean(user && index && user.pubkey === index.pubkey);
  const image = index ? sanitizeUrl(publicationImage(index)) : undefined;
  const authors = index ? getPublicationAuthors(index) : [];
  const identifiers = index ? getPublicationIdentifiers(index) : [];
  const topics = index ? getPublicationTopics(index) : [];
  const version = index ? publicationVersion(index) : undefined;
  const publishedOn = index ? publicationPublishedOn(index) : undefined;
  const publishedBy = index ? publicationPublishedBy(index) : undefined;
  const source = index ? sanitizeUrl(publicationSource(index)) : undefined;
  const derivativeWork = index ? getDerivativeWorkInfo(index) : undefined;
  const hasMetadataRow = Boolean(
    version || publishedOn || publishedBy || source || identifiers.length > 0 || topics.length > 0,
  );

  const { detected, selected, setSelected } = useContentFormat(displayedSection?.content ?? '');

  return (
    <Layout>
      <div className="mx-auto max-w-6xl px-4 py-8">
        {isLoading ? (
          <PublicationSkeleton />
        ) : !index ? (
          <Card className="border-dashed">
            <CardContent className="py-12 px-8 text-center">
              <p className="mx-auto max-w-sm text-muted-foreground">
                No publication found for this address. It may not exist, or hasn't reached your relays yet.
              </p>
            </CardContent>
          </Card>
        ) : (
          <>
            <div className="mb-6 flex flex-wrap items-start justify-between gap-4 border-b border-border pb-6">
              <div className="flex min-w-0 gap-4">
                {image && (
                  <img src={image} alt="" className="hidden size-24 shrink-0 rounded-lg object-cover sm:block" />
                )}
                <div className="min-w-0">
                  <div className="mb-2 flex flex-wrap items-center gap-2">
                    <Badge variant="secondary" className="capitalize">
                      <BookOpen className="size-3" />
                      {publicationType(index)}
                    </Badge>
                    {isOfflineCopy && (
                      <Badge variant="secondary" className="gap-1.5">
                        <WifiOff className="size-3" />
                        Offline copy
                      </Badge>
                    )}
                  </div>
                  <h1 className="text-3xl font-bold tracking-tight break-words">{publicationTitle(index)}</h1>
                  {publicationSummary(index) && (
                    <p className="mt-2 max-w-2xl break-words text-muted-foreground">{publicationSummary(index)}</p>
                  )}
                  <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-1 text-sm text-muted-foreground">
                    {authors.length > 0 ? (
                      <span className="flex items-center gap-1.5">
                        <User className="size-3.5" />
                        {authors.map((a) => (a.role ? `${a.name} (${a.role})` : a.name)).join(', ')}
                      </span>
                    ) : (
                      <Link to={`/${npub}`} className="flex items-center gap-1.5 hover:text-foreground">
                        <User className="size-3.5" />
                        {displayName}
                      </Link>
                    )}
                    {version && (
                      <span className="flex items-center gap-1.5">
                        <BookOpen className="size-3.5" />
                        {version}
                      </span>
                    )}
                    {(publishedOn || publishedBy) && (
                      <span className="flex items-center gap-1.5">
                        <Calendar className="size-3.5" />
                        {[publishedOn, publishedBy].filter(Boolean).join(' · ')}
                      </span>
                    )}
                  </div>

                  {derivativeWork && (
                    <p className="mt-2 text-xs text-muted-foreground">
                      Derived from a work by{' '}
                      <Link
                        to={`/${nip19.npubEncode(derivativeWork.originalAuthorPubkey)}`}
                        className="underline underline-offset-2 hover:text-foreground"
                      >
                        {nip19.npubEncode(derivativeWork.originalAuthorPubkey).slice(0, 12)}…
                      </Link>
                    </p>
                  )}

                  {hasMetadataRow && (
                    <div className="mt-3 flex flex-wrap items-center gap-2">
                      {source && (
                        <a
                          href={source}
                          target="_blank"
                          rel="noopener noreferrer"
                          className="flex items-center gap-1 text-xs text-muted-foreground underline underline-offset-2 hover:text-foreground"
                        >
                          <Link2 className="size-3" />
                          Original source
                        </a>
                      )}
                      {identifiers.map((id) => {
                        const url = identifierUrl(id);
                        const label = `${id.type}: ${id.value}`;
                        return url ? (
                          <a
                            key={`${id.type}:${id.value}`}
                            href={url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="inline-flex"
                          >
                            <Badge variant="outline" className="text-xs">
                              {label}
                            </Badge>
                          </a>
                        ) : (
                          <Badge key={`${id.type}:${id.value}`} variant="outline" className="text-xs">
                            {label}
                          </Badge>
                        );
                      })}
                      {topics.map((topic) => (
                        <Badge key={topic} variant="secondary" className="text-xs">
                          <Tag className="size-3" />
                          {topic}
                        </Badge>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div className="flex w-full flex-col items-start gap-2 sm:w-auto sm:items-end">
                <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
                  <StarButton event={index} addressPointer={`${PUBLICATION_INDEX_KIND}:${index.pubkey}:${d}`} />
                  <ZapButton event={index} />
                  <CommentsDialog event={index} title={publicationTitle(index)} />
                  {isOwn && (
                    <Button variant="outline" size="sm" asChild>
                      <Link to={`/p/${encodeURIComponent(d)}/edit`}>
                        <PenSquare className="size-4" />
                        Edit publication
                      </Link>
                    </Button>
                  )}
                  <OfflinePublicationSaveButton
                    index={index}
                    sections={sections}
                    sectionsLoading={sectionsLoading}
                  />
                  <PublicationExportMenu index={index} sections={sections} sectionsLoading={sectionsLoading} />
                  <ShareDialog event={index} title={publicationTitle(index)} />
                  <EventDetailsDialog event={index} title={publicationTitle(index)} />
                </div>
                {displayedSection && displayedSection.kind !== PUBLICATION_INDEX_KIND && (
                  <ContentFormatSelect value={selected} detected={detected} onChange={setSelected} />
                )}
              </div>
            </div>

            <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
              <nav aria-label="Table of contents" className="min-w-0 lg:sticky lg:top-20 lg:self-start">
                <h2 className="mb-2 text-sm font-semibold text-muted-foreground">Contents</h2>
                {!sections || sections.length === 0 ? (
                  <p className="text-sm text-muted-foreground">This publication has no sections yet.</p>
                ) : (
                  <ol className="space-y-1 border-l border-border pl-3">
                    {sections.map((section, i) => (
                      <li key={section.id}>
                        <TocLink
                          section={section}
                          index={i}
                          d={d}
                          npub={npub}
                          active={displayedSection?.id === section.id}
                        />
                      </li>
                    ))}
                  </ol>
                )}
              </nav>

              <article className="min-w-0">
                {displayedSection ? (
                  <SectionContent key={displayedSection.id} event={displayedSection} format={selected} />
                ) : (
                  <Card className="border-dashed">
                    <CardContent className="py-12 px-8 text-center">
                      <p className="mx-auto max-w-sm text-muted-foreground">
                        This publication doesn't have any readable sections yet.
                      </p>
                    </CardContent>
                  </Card>
                )}
              </article>
            </div>
          </>
        )}
      </div>
    </Layout>
  );
}

function TocLink({
  section,
  index,
  d,
  npub,
  active,
}: {
  section: NostrEvent;
  index: number;
  d: string;
  npub: string;
  active: boolean;
}) {
  const sectionD = section.tags.find(([n]) => n === 'd')?.[1] ?? '';
  const isSubIndex = section.kind === PUBLICATION_INDEX_KIND;
  const title = isSubIndex ? publicationTitle(section) : sectionTitle(section);

  const to = isSubIndex
    ? `/p/${encodeURIComponent(sectionD)}/${nip19.npubEncode(section.pubkey)}`
    : `/p/${encodeURIComponent(d)}/${npub}/${encodeURIComponent(sectionD)}`;

  return (
    <Link
      to={to}
      className={`block rounded-md px-2 py-1.5 text-sm break-words transition-colors ${
        active ? 'bg-accent font-medium text-accent-foreground' : 'text-muted-foreground hover:bg-accent/50 hover:text-foreground'
      }`}
    >
      <span className="mr-1.5 text-xs text-muted-foreground/70">{index + 1}.</span>
      {title}
      {isSubIndex && <BookOpen className="ml-1.5 inline size-3" />}
    </Link>
  );
}

function SectionContent({ event, format }: { event: NostrEvent; format: ViewFormat }) {
  const [highlightMode, setHighlightMode] = useState(false);
  const [readerMode, setReaderMode] = useState(false);

  if (event.kind === PUBLICATION_INDEX_KIND) {
    return (
      <Card className="border-dashed">
        <CardContent className="py-8 px-6 text-center">
          <p className="mx-auto max-w-sm text-muted-foreground">
            "{publicationTitle(event)}" is a nested publication.
          </p>
          <Button className="mt-4" variant="outline" asChild>
            <Link to={`/p/${encodeURIComponent(event.tags.find(([n]) => n === 'd')?.[1] ?? '')}/${nip19.npubEncode(event.pubkey)}`}>
              <ExternalLink className="size-4" />
              Open nested publication
            </Link>
          </Button>
        </CardContent>
      </Card>
    );
  }

  // Both 30041 sections and referenced 30817/30818/30023 leaves go through
  // the same format-detecting viewer — publication content isn't
  // guaranteed to be AsciiDoc just because NKBIP-01 recommends it.
  const sectionD = getTagValue(event, 'd');
  const addressPointer = sectionD ? `${event.kind}:${event.pubkey}:${sectionD}` : undefined;

  return (
    <>
      <div className="mb-4 flex flex-wrap items-start justify-between gap-3">
        <h1 className="min-w-0 text-2xl font-bold tracking-tight break-words">{sectionTitle(event)}</h1>
        {sectionD && (
          <div className="flex w-full flex-wrap items-center gap-2 sm:w-auto sm:justify-end">
            <StarButton event={event} addressPointer={addressPointer} />
            <ZapButton event={event} />
            <CommentsDialog event={event} title={sectionTitle(event)} />
            <HighlightToggle
              event={event}
              addressPointer={addressPointer}
              enabled={highlightMode}
              onToggle={setHighlightMode}
            />
            <ReaderModeToggle enabled={readerMode} onToggle={setReaderMode} />
            <ExportMenu event={event} title={sectionTitle(event)} format={format} />
            <ShareDialog event={event} title={sectionTitle(event)} />
            <EventDetailsDialog event={event} title={sectionTitle(event)} />
          </div>
        )}
      </div>
      <HighlightLayer event={event} addressPointer={addressPointer} active={highlightMode}>
        <ContentBody event={event} format={format} />
      </HighlightLayer>

      {readerMode && (
        <ReaderModeView title={sectionTitle(event)} onClose={() => setReaderMode(false)}>
          <ContentBody event={event} format={format} />
        </ReaderModeView>
      )}
    </>
  );
}

function PublicationSkeleton() {
  return (
    <div className="grid gap-8 lg:grid-cols-[280px_1fr]">
      <div className="min-w-0 space-y-2">
        <Skeleton className="h-4 w-24" />
        {Array.from({ length: 5 }).map((_, i) => (
          <Skeleton key={i} className="h-6 w-full" />
        ))}
      </div>
      <div className="space-y-3">
        <Skeleton className="h-9 w-2/3" />
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-full" />
      </div>
    </div>
  );
}
